import Header from './components/header';
import Footer from './components/footer';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '@src/components/layout/Layout';
import PdfViewer from '@src/components/utilities/PDFViewer';
import { useSaveScanDecisionLazy } from '@src/adapters/hooks/scans/useSaveScanDecision';
import ScanConsultantReviewDTO from '@src/adapters/dto/scan/ScaneConsultantReviewDTO';
import { useFindScansUnreviewedByConsultantLazy } from '@src/adapters/hooks/scans/useFindScansUnreviewedByConsultant';
import { useAuth } from '@src/auth/hooks/useAuth';
import IdNameDto from '@src/adapters/dto/shared/IdNameDto';
import ScanProcedureDTO from '@src/adapters/dto/scan/ScanProcedureDTO';
import Reassign from './components/reassign';
import { Typography } from '@mui/material';
import ConsultantName from '@src/components/user/ConsultantName';
import { Routes } from '@src/navigation/Routes';
import { CardInfoStatus } from '@src/constants/CardInfoStatus';

function ConsultantReview() {
  const location = useLocation();
  const navigate = useNavigate();
  const { execute: getScans } = useFindScansUnreviewedByConsultantLazy();
  const { execute: saveDecision } = useSaveScanDecisionLazy();
  const { currentHospitalId } = useAuth();
  const [scans, setScans] = useState<ScanConsultantReviewDTO[]>([]);
  const [procedures, setProcedures] = useState<ScanProcedureDTO[]>([]);
  const [selectedItem, setSelectedItem] = useState<ScanConsultantReviewDTO>();
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [numberOfPages, setNumberOfPages] = useState<number>();
  const [rotation, setRotation] = useState(0);
  const [selectedProc, setSelectedProc] = useState<ScanProcedureDTO>();
  const [isLoading, setIsloading] = useState(false);
  const ZERO_GUID = '00000000-0000-0000-0000-000000000000';
  const [reassignOpen, setReassignOpen] = useState(false);

  useEffect(() => {
    const added = new Set<string>();

    const uniqueProcedures = scans
      .map((a) => a.procedure)
      .filter((p): p is IdNameDto => {
        if (!p || added.has(p.id)) return false;
        added.add(p.id);
        return true;
      })
      .map((p) => {
        return { id: p.id, name: p.name };
      })
      .sort((a, b) => -b.name.localeCompare(a.name));

    const unassignedCount = scans.filter((a) => !a.procedure).length;

    if (unassignedCount > 0) {
      uniqueProcedures.push({
        id: ZERO_GUID,
        name: '**** No Procedure Set ****',
      });
    }

    let procedures = uniqueProcedures.map((p) => ({
      id: p.id,
      name: p.name,
      scans: scans.filter((a) =>
        p.id === ZERO_GUID
          ? !a.procedure
          : a.procedure && a.procedure.id === p.id
      ),
    }));

    setProcedures(procedures);
  }, [scans, saveDecision]);

  const indexedScans = useMemo(() => {
    return procedures.flatMap((procedure) => procedure.scans);
  }, [procedures]);

  const consultantId = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get('consultantId');
  }, [location.search]);

  useEffect(() => {
    let isMounted = true;
    setIsloading(true);
    const search = async () => {
      if (consultantId && isMounted) {
        getScans({ consultantId, hospitalId: currentHospitalId }).then(
          (result) => {
            if (result.scans.length > 0) {
              const sortedScans = result.scans.sort(
                (a, b) => -b.originalFilename.localeCompare(a.originalFilename)
              );
              setScans(sortedScans);
            }
          }
        );
      }
    };
    search();
    return () => {
      isMounted = false;
    };
  }, [location.search, getScans, currentHospitalId, consultantId]);

  useEffect(() => {
    if (procedures && procedures.length > 0 && isLoading) {
      setSelectedProc(procedures[0]);
      setSelectedItem(procedures[0].scans[0]);
      setSelectedIndex(0);
      setIsloading(false);
    }
  }, [procedures, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      const itemToSelect = indexedScans[selectedIndex];
      const proc = procedures.find((p) =>
        p.scans.find((a) => a.id === itemToSelect.id)
      );

      setSelectedProc(proc);
      setSelectedItem(itemToSelect);
    }
  }, [indexedScans, selectedIndex, procedures, isLoading]);

  const handleMove = (forward: boolean) => {
    setSelectedIndex((prev) => {
      if (prev === undefined) return prev;
      let newIndex = forward ? prev + 1 : prev - 1;
      newIndex = Math.max(0, Math.min(newIndex, scans.length - 1));
      return newIndex;
    });
  };

  const onMoveScan = (forward: boolean) => {
    setSelectedIndex((prev) => {
      if (prev === undefined) {
        return prev;
      }

      let newIndex = forward ? prev + 1 : prev - 1;
      newIndex = Math.max(0, Math.min(newIndex, scans.length - 1));

      const prevScan = indexedScans[prev];
      const newScan = indexedScans[newIndex];

      const prevProcId = procedures.find((p) =>
        p.scans.find((a) => a.id === prevScan.id)
      )?.id;

      const nextProcId = procedures.find((p) =>
        p.scans.find((a) => a.id === newScan.id)
      )?.id;

      if (prevProcId === nextProcId) {
        return newIndex;
      } else {
        return prev;
      }
    });
  };

  const onSelectScan = (scan: ScanConsultantReviewDTO) => {
    setSelectedIndex(findIndex(scan.id));
  };

  const onReassign = () => {
    setReassignOpen(true);
  };

  const onMigrate = (isMigrate: boolean) => {
    if (selectedItem) {
      saveDecision({
        scanId: selectedItem.id,
        isMigrate: isMigrate,
      }).then((result) => {
        setScans((prev) => {
          const previousScans = [...prev];
          return previousScans.map((a) => {
            const found = result.updatedItems.find(
              (updatedItem) => updatedItem.id === a.id
            );
            if (found) {
              const ab = {
                ...a,
                isDoNotMigrate: found.status === CardInfoStatus.doNotMigrate,
                isExtraFile: found.status === CardInfoStatus.extraFile,
                isMigrate: found.status === CardInfoStatus.migrate,
              };
              return ab;
            } else {
              return a;
            }
          });
        });
      });
    }
  };

  const handleZoom = (increase: boolean) => {
    if (increase) {
      setZoomLevel((prev) => prev + 0.5);
    } else {
      setZoomLevel((prev) => (prev - 0.5 < 0.5 ? 0.5 : prev - 0.5));
    }
  };

  const findIndex = (id: string) => {
    return indexedScans.findIndex((scan) => scan.id === id);
  };

  const onSelectProcedure = (proc: ScanProcedureDTO) => {
    setSelectedItem(undefined);
    setSelectedProc(proc);
    setSelectedItem(proc.scans[0]);
    setSelectedIndex(findIndex(proc.scans[0].id));
  };

  const handleCloseReassign = () => {
    setReassignOpen(false);
  };

  const handleReassigned = (
    updatedConsultantId: string,
    scan: ScanConsultantReviewDTO
  ) => {
    if (consultantId !== updatedConsultantId) {
      if (scans.length === 1) {
        navigate(Routes.ScanReview);
      } else {
        setScans((prev) => prev.filter((a) => a.id !== scan.id));
      }
    } else {
      setScans((prev) => {
        const newScans = prev.map((a) =>
          a.id !== scan.id
            ? a
            : {
                ...a,
                procedure: scan.procedure,
                isMigrate: scan.isMigrate,
              }
        );

        if (scan.procedure && scan.procedure.id) {
          const procedureScans = newScans.filter(
            (a) => a.procedure && a.procedure!.id === scan.procedure?.id
          );

          if (procedureScans.length > 1) {
            return newScans.map((a) =>
              a.procedure && a.procedure.id === scan.procedure!.id
                ? {
                    ...a,
                    isDoNotMigrate: false,
                    isMigrate: false,
                    isExtraFile: false,
                  }
                : a
            );
          }
        }
        return newScans;
      });
    }

    setReassignOpen(false);
  };

  return (
    <Layout>
      {consultantId && (
        <Typography variant="h4">
          <ConsultantName id={consultantId} />
        </Typography>
      )}
      <Header
        procedures={procedures}
        selectedProc={selectedProc}
        selectedItem={selectedItem}
        selectedIndex={selectedIndex}
        onMoveScan={onMoveScan}
        onSelectScan={onSelectScan}
        onSelectProcedure={onSelectProcedure}
        onMigrate={onMigrate}
        onReassign={onReassign}
      />
      {selectedItem && (
        <PdfViewer
          pdfUrl={selectedItem.fileURL}
          setNumberOfPages={setNumberOfPages}
          pageNumber={pageNumber}
          rotation={rotation}
          zoomLevel={zoomLevel}
          heightReduction={4}
        />
      )}
      {numberOfPages && (
        <Footer
          procedures={procedures}
          numberOfPages={numberOfPages}
          currentPage={pageNumber}
          selectedIndex={selectedIndex}
          setPageNumber={setPageNumber}
          previous={() => handleMove(false)}
          next={() => handleMove(true)}
          updateRotation={setRotation}
          zoom={handleZoom}
        />
      )}
      {selectedItem && (
        <Reassign
          scanId={selectedItem.id}
          currentConsultantId={consultantId}
          currentProcedure={selectedProc}
          open={reassignOpen}
          close={handleCloseReassign}
          reassigned={handleReassigned}
        />
      )}
    </Layout>
  );
}

export default ConsultantReview;
